import { ChevronDown, ChevronUp, X, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useSectionsBuilderStore, type SectionType, type DensityType } from '../../stores/sections-builder-store';
import { useLanguageStore } from '../../stores/language-store';
import { useGenerationStore } from '../../stores/generation-store';
import { useProjectsStore } from '../../stores/projects-store';
import { t } from '../../i18n/translations';
import { constructPromptWithSettings, buildSectionsPrompt } from '../../utils/prompt-utils';
import { PromptPreviewDialog } from './PromptPreviewDialog';

export type LayoutType = 'web' | 'mobile';

const WEB_SECTIONS: SectionType[] = [
  'header',
  'hero',
  'features',
  'cards',
  'testimonials',
  'pricing',
  'faq',
  'bottomNav',
  'footer',
];

export type MobileSectionType =
  | 'tabBar'
  | 'drawer'
  | 'splashScreen'
  | 'listFeed'
  | 'detailScreen'
  | 'form'
  | 'modal'
  | 'loadingState'
  | 'emptyState'
  | 'errorScreen';

const MOBILE_SECTIONS: MobileSectionType[] = [
  'tabBar',
  'drawer',
  'splashScreen',
  'listFeed',
  'detailScreen',
  'form',
  'modal',
  'loadingState',
  'emptyState',
  'errorScreen',
];

const DENSITIES: DensityType[] = ['minimal', 'standard', 'rich'];

const sectionLabelKey: Record<SectionType, 'sectionHeader' | 'sectionHero' | 'sectionFeatures' | 'sectionCards' | 'sectionTestimonials' | 'sectionPricing' | 'sectionFaq' | 'sectionBottomNav' | 'sectionFooter'> = {
  header: 'sectionHeader',
  hero: 'sectionHero',
  features: 'sectionFeatures',
  cards: 'sectionCards',
  testimonials: 'sectionTestimonials',
  pricing: 'sectionPricing',
  faq: 'sectionFaq',
  bottomNav: 'sectionBottomNav',
  footer: 'sectionFooter',
};

const mobileSectionLabelKey: Record<MobileSectionType, 'mobileSectionTabBar' | 'mobileSectionDrawer' | 'mobileSectionSplash' | 'mobileSectionListFeed' | 'mobileSectionDetail' | 'mobileSectionForm' | 'mobileSectionModal' | 'mobileSectionLoading' | 'mobileSectionEmpty' | 'mobileSectionError'> = {
  tabBar: 'mobileSectionTabBar',
  drawer: 'mobileSectionDrawer',
  splashScreen: 'mobileSectionSplash',
  listFeed: 'mobileSectionListFeed',
  detailScreen: 'mobileSectionDetail',
  form: 'mobileSectionForm',
  modal: 'mobileSectionModal',
  loadingState: 'mobileSectionLoading',
  emptyState: 'mobileSectionEmpty',
  errorScreen: 'mobileSectionError',
};

const densityLabelKey: Record<DensityType, 'densityMinimal' | 'densityStandard' | 'densityRich'> = {
  minimal: 'densityMinimal',
  standard: 'densityStandard',
  rich: 'densityRich',
};

export function SectionsBuilder() {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [layoutType, setLayoutType] = useState<LayoutType>('web');
  const language = useLanguageStore((s) => s.language);
  const selectedSections = useSectionsBuilderStore((s) => s.selectedSections);
  const density = useSectionsBuilderStore((s) => s.density);
  const isExpanded = useSectionsBuilderStore((s) => s.isExpanded);
  const toggleSection = useSectionsBuilderStore((s) => s.toggleSection);
  const setDensity = useSectionsBuilderStore((s) => s.setDensity);
  const setExpanded = useSectionsBuilderStore((s) => s.setExpanded);
  const clearSections = useSectionsBuilderStore((s) => s.clearSections);
  const prompt = useGenerationStore((s) => s.prompt);
  const currentProject = useProjectsStore((s) => s.currentProject);

  const hasSelections = selectedSections.length > 0;

  const buildFinalPrompt = (): string => {
    const userPrompt = constructPromptWithSettings(prompt, currentProject?.generationSettings);
    const sectionsPrompt = buildSectionsPrompt(selectedSections, density, language);
    return `${userPrompt}${sectionsPrompt}`;
  };

  const handleLayoutChange = (newType: LayoutType) => {
    setLayoutType(newType);
    clearSections();
  };

  return (
    <div className="border-b border-surface-lighter">
      <button
        onClick={() => setExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-text-muted hover:text-text hover:bg-surface-light transition-colors"
      >
        <span className="flex items-center gap-2">
          <span>{t(language, 'sectionsBuilder')}</span>
          {hasSelections && (
            <span className="px-1.5 py-0.5 rounded bg-accent/20 text-accent text-[10px]">
              {selectedSections.length}
            </span>
          )}
        </span>
        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-3">
          {/* Layout Type Selector */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-text-muted">{t(language, 'layoutType')}:</span>
            <div className="flex gap-1">
              {(['web', 'mobile'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => handleLayoutChange(type)}
                  className={`px-2 py-0.5 text-[11px] rounded transition-colors ${
                    layoutType === type
                      ? 'bg-accent/20 text-accent'
                      : 'text-text-muted hover:text-text'
                  }`}
                >
                  {type === 'web' ? 'Web' : 'Mobile App'}
                </button>
              ))}
            </div>
          </div>

          {/* Presets */}
          {layoutType === 'web' && (
            <div className="space-y-1">
              <p className="text-[10px] text-text-muted uppercase tracking-wide">Web presets</p>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => {
                    clearSections();
                    setTimeout(() => {
                      ['header', 'hero', 'features', 'bottomNav', 'footer'].forEach((s) => {
                        toggleSection(s as SectionType);
                      });
                    }, 0);
                  }}
                  className="px-2 py-1 text-xs rounded-full bg-surface-light border border-surface-lighter text-text-muted hover:text-text hover:border-text-muted transition-colors"
                >
                  Standard
                </button>
                <button
                  onClick={() => {
                    clearSections();
                    setTimeout(() => {
                      ['header', 'hero', 'features', 'testimonials', 'bottomNav', 'footer'].forEach((s) => {
                        toggleSection(s as SectionType);
                      });
                    }, 0);
                  }}
                  className="px-2 py-1 text-xs rounded-full bg-surface-light border border-surface-lighter text-text-muted hover:text-text hover:border-text-muted transition-colors"
                >
                  With reviews
                </button>
                <button
                  onClick={() => {
                    clearSections();
                    setTimeout(() => {
                      ['header', 'hero', 'features', 'pricing', 'faq', 'bottomNav', 'footer'].forEach((s) => {
                        toggleSection(s as SectionType);
                      });
                    }, 0);
                  }}
                  className="px-2 py-1 text-xs rounded-full bg-surface-light border border-surface-lighter text-text-muted hover:text-text hover:border-text-muted transition-colors"
                >
                  Product page
                </button>
              </div>
            </div>
          )}

          {layoutType === 'mobile' && (
            <div className="space-y-1">
              <p className="text-[10px] text-text-muted uppercase tracking-wide">App presets</p>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => {
                    clearSections();
                    setTimeout(() => {
                      (['tabBar', 'listFeed', 'detailScreen'] as const).forEach((s) => {
                        toggleSection(s);
                      });
                    }, 0);
                  }}
                  className="px-2 py-1 text-xs rounded-full bg-surface-light border border-surface-lighter text-text-muted hover:text-text hover:border-text-muted transition-colors"
                >
                  Basic app
                </button>
                <button
                  onClick={() => {
                    clearSections();
                    setTimeout(() => {
                      (['splashScreen', 'tabBar', 'listFeed', 'detailScreen', 'modal'] as const).forEach((s) => {
                        toggleSection(s);
                      });
                    }, 0);
                  }}
                  className="px-2 py-1 text-xs rounded-full bg-surface-light border border-surface-lighter text-text-muted hover:text-text hover:border-text-muted transition-colors"
                >
                  With modals
                </button>
                <button
                  onClick={() => {
                    clearSections();
                    setTimeout(() => {
                      (['splashScreen', 'drawer', 'listFeed', 'detailScreen', 'form', 'loadingState', 'emptyState', 'errorScreen'] as const).forEach((s) => {
                        toggleSection(s);
                      });
                    }, 0);
                  }}
                  className="px-2 py-1 text-xs rounded-full bg-surface-light border border-surface-lighter text-text-muted hover:text-text hover:border-text-muted transition-colors"
                >
                  Full featured
                </button>
              </div>
            </div>
          )}

          <div className="border-t border-surface-lighter pt-3">
            <p className="text-[11px] text-text-muted">{t(language, 'sectionsBuilderHint')}</p>
          </div>

          {/* Section chips */}
          <div className="flex flex-wrap gap-1.5">
            {layoutType === 'web'
              ? WEB_SECTIONS.map((section) => {
                  const isSelected = selectedSections.includes(section);
                  return (
                    <button
                      key={section}
                      onClick={() => toggleSection(section)}
                      className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                        isSelected
                          ? 'bg-accent text-white border-accent'
                          : 'bg-surface border-surface-lighter text-text-muted hover:text-text hover:border-text-muted'
                      }`}
                    >
                      {t(language, sectionLabelKey[section])}
                    </button>
                  );
                })
              : MOBILE_SECTIONS.map((section) => {
                  const isSelected = selectedSections.includes(section);
                  return (
                    <button
                      key={section}
                      onClick={() => toggleSection(section)}
                      className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                        isSelected
                          ? 'bg-accent text-white border-accent'
                          : 'bg-surface border-surface-lighter text-text-muted hover:text-text hover:border-text-muted'
                      }`}
                    >
                      {t(language, mobileSectionLabelKey[section])}
                    </button>
                  );
                })}
          </div>

          {/* Density selector */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-text-muted">{t(language, 'densityLabel')}:</span>
            <div className="flex gap-1">
              {DENSITIES.map((d) => (
                <button
                  key={d}
                  onClick={() => setDensity(d)}
                  className={`px-2 py-0.5 text-[11px] rounded transition-colors ${
                    density === d
                      ? 'bg-accent/20 text-accent'
                      : 'text-text-muted hover:text-text'
                  }`}
                >
                  {t(language, densityLabelKey[d])}
                </button>
              ))}
            </div>
          </div>

          {/* Clear and Preview buttons */}
          <div className="flex items-center gap-2">
            {hasSelections && (
              <button
                onClick={clearSections}
                className="flex items-center gap-1 px-2 py-1 rounded border border-red-400/30 text-[11px] text-text-muted hover:text-red-400 hover:border-red-400/50 transition-colors"
              >
                <X size={12} />
                {t(language, 'clearSections')}
              </button>
            )}
            {prompt.trim() && (
              <button
                onClick={() => setPreviewOpen(true)}
                className="flex items-center gap-1 px-2 py-1 rounded border border-accent/30 text-[11px] text-text-muted hover:text-accent hover:border-accent/50 transition-colors"
                title={t(language, 'previewTooltip')}
              >
                <Sparkles size={12} />
                {t(language, 'previewPrompt')}
              </button>
            )}
          </div>
        </div>
      )}
      <PromptPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        finalPrompt={buildFinalPrompt()}
      />
    </div>
  );
}
